import { isBefore, isAfter, isSameDay, parseISO } from "date-fns";
import { SurveyWithExtras, isSurveyArchived } from "../interfaces";
import {
  buildOrderTypeLabelsBySurveyId,
  normalizeForCompare,
} from "../utils/orderTypeLabel";

export class Survey {
  static react: any;
  class: typeof Survey;
  react: any;
  navigate: (path: string) => void;
  id: number;
  label: string;
  folder_id: number | null;
  site_id?: number | null;
  defaultAnnotationLayerId: number | null;
  locked_by_id?: number;
  deleted_at?: string | null;

  public constructor(apiData, getState, setState, navigate) {
    const apiDataWhitelist = [
      "id",
      "organization_id",
      "label",
      "nominal_flight_date",
      "folder_id",
      "site_id",
      "zoom_start",
      "coordinates",
      "group_ids",
      "timezone",
      "status",
      "location",
      "level",
      "locked_time",
      "locked_by_id",
      "locked_by_name",
      "created_by",
      "created_by_name",
      "start_time",
      "end_time",
      "created_at",
      "tags",
      "deleted_at",
    ];

    (Object as any).entries(apiData).forEach(([k, v]) => {
      if (apiDataWhitelist.includes(k)) {
        this[k] = v;
      } else {
        console.warn(
          `Attempted to attach unknown attribute to survey model: <<${k}>>. If this is deliberate, it must be added to the data whitelist in the Survey model.`
        );
      }
    });

    this.react = {
      getState,
      setState,
    };

    this.navigate = navigate;

    this.class = Survey;
    this.select = this.select.bind(this);
    this.unselect = this.unselect.bind(this);
    this.focus = this.focus.bind(this);
    this.unfocus = this.unfocus.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.defaultAnnotationLayerId = null;
  }

  public get layers() {
    return this.react.getState().layers.filter((l) => l.survey_id === this.id);
  }
  public get annotationLayers() {
    return this.react
      .getState()
      .layers.filter((l) => l.survey_id === this.id && l.type === "annotation");
  }

  public get isASurvey() {
    return true;
  }
  public get isSelected() {
    return this.react.getState().selectedSurveyId === this.id;
  }
  public get isFocused() {
    return this.id === this.react.getState().focusedSurveyId;
  }
  public get isInFilterSelection() {
    return (
      !!Survey.byDateRange([this])[0] &&
      !!Survey.bySearchString([this])[0] &&
      !!Survey.byUsers([this])[0] &&
      !!Survey.byArchived([this])[0]
    );
  }
  /*
    Set as _the_ selected survey in the Portal. There is only one selected survey at a time.
  */
  public select(): void {
    this.open();
    this.react.setState((prev) => ({
      ...prev,
      mapState: { ...prev.mapState, selectedStyle: "Satellite" },
    }));
    this.navigate(`/surveys/${this.id}`);

    // Drop the label after an interval
    setTimeout(this.unfocus, 3000);
  }

  /*
    Deselects the current survey. Does nothing if the current survey is not the selected one. Should be idempotent.
  */
  public unselect(): void {
    if (this.react.getState().selectedSurveyId === this.id) {
      this.react.setState({ selectedSurveyId: null });
      // this.poke() TODO: If the year is 2020 and nobody seems to have noticed that this poke was necessary, go ahead and remove it.;
    }
  }

  public focus(): void {
    this.react.setState({ focusedSurveyId: this.id });
    // this.poke() TODO: If the year is 2020 and nobody seems to have noticed that this poke was necessary, go ahead and remove it.;
  }

  /*
    Unfocus the current survey. Does nothing if the current survey is not the focused one. Should be idempotent.
  */
  public unfocus(): void {
    if (this.react.getState().focusedSurveyId === this.id) {
      this.react.setState({ focusedSurveyId: null });
      // this.poke() TODO: If the year is 2020 and nobody seems to have noticed that this poke was necessary, go ahead and remove it.;
    }
  }

  /*
    The Portal maintains a list of which surveys are "open." This adds the current survey to that list if it is not
    already included. Should be idempotent.
  */
  public open(): void {
    if (this.react.getState().openSurveyIds.includes(this.id)) return;

    // Provisionary behavior: Opening a survey replaces any existing open surveys
    this.react.setState((prev) => ({ openSurveyIds: [this.id] }));

    // Sidelined behavior: Multiple surveys can be open at a time
    // this.react.setState(prev => ({ openSurveyIds: [...prev.openSurveyIds, this.id] }));
  }

  /*
    The Portal maintains a list of which surveys are "open." This removes the current survey from that list, if it
    is in it. It also unselects the current survey if it is selected.

    TODO: There is a side effect where, if this survey is to be unselected, a new survey may be selected. This is a holdover
    from when this method was part of the Portal component, and that logic should find a new home ASAP.
  */
  public close() {
    // Remove this survey from the list of open surveys
    this.react.setState((prev) => ({
      openSurveyIds: prev.openSurveyIds.filter((id) => id !== this.id),
    }));

    /* TODO: This "select the next survey" logic was basically copy-pasta'd from a Portal method...
    Because "next survey" is a consequence of which surveys have been opened by the user, this logic is too tightly
    entangled with the UI state to be a good model method. It should find a new home ASAP.
     */
    if (this.isSelected) {
      this.unselect();
      // "Grab the survey before the current selectedSurvey, and if there are none before, set to null."
      const previousSurveyId =
        this.react.getState().openSurveyIds[
          this.react.getState().openSurveyIds.indexOf(this.id) - 1
        ] || null;
      const previousSurvey =
        previousSurveyId &&
        this.react.getState().surveys.find((s) => s.id === previousSurveyId);
      previousSurvey
        ? previousSurvey.select()
        : this.react.setState({ selectedSurveyId: null });
    }
  }

  // public setDefaultAnnotationLayer(layerId: number) {
  //   const targetLayer = this.annotationLayers.find((l) => l.id === layerId);
  //   if (!targetLayer) {
  //     return console.warn(
  //       `⚠️ Tried to set default annotation layer for survey ${this.id} to layer with ID ${layerId}, but layer was not found`
  //     );
  //   }
  //   this.defaultAnnotationLayerId = layerId;
  //   // this.poke() TODO: If the year is 2020 and nobody seems to have noticed that this poke was necessary, go ahead and remove it.;
  //   // targetLayer.poke();
  // }

  // public resetDefaultAnnotationLayer() {
  //   this.defaultAnnotationLayerId = null;
  // }

  // Perturbs the collection to trigger a re-render. If this becomes a performance bottleneck, we'll need to
  // find a way to perturb a single item instead of an entire collection.
  private static get all(): Survey[] {
    return this.react.getState().surveys;
  }

  private poke() {
    this.react.setState((prev) => ({ surveys: [...prev.surveys] }));
  }

  public static connect(getState, setState) {
    this.react = { getState, setState };
  }

  public static find(id: number): Survey | undefined {
    return Survey.react.getState().surveys.find((s) => s.id === id);
  }

  public static byAllFilters(_surveys?: Survey[], options) {
    const surveys = _surveys || this.all;

    const folderMatches = this.byFolders(surveys);
    const siteMatches = this.bySites(surveys);
    const hasFolderFilter =
      (this.react.getState().surveyListFilters.selectedFolderIds?.length ?? 0) >
      0;
    const hasSiteFilter =
      (this.react.getState().surveyListFilters.selectedSiteIds?.length ?? 0) > 0;

    let locationFiltered: Survey[];
    if (hasFolderFilter && hasSiteFilter) {
      const idSet = new Set<number>();
      locationFiltered = [];
      for (const s of [...folderMatches, ...siteMatches]) {
        if (!idSet.has(s.id)) {
          idSet.add(s.id);
          locationFiltered.push(s);
        }
      }
    } else if (hasFolderFilter) {
      locationFiltered = folderMatches;
    } else if (hasSiteFilter) {
      locationFiltered = siteMatches;
    } else {
      locationFiltered = surveys;
    }

    const andFilters = [
      this.bySearchString.bind(this),
      this.byDateRange.bind(this),
      this.byUsers.bind(this),
      this.byTags.bind(this),
      this.byStatus.bind(this),
      this.byOrderType.bind(this),
      this.byArchived.bind(this),
      this.byMapViewport.bind(this),
    ];

    return andFilters.reduce(
      (result, filter) => filter(result, options),
      locationFiltered
    );
  }

  public static byDateRange(_surveys?: Survey[], options?): Survey[] {
    const state = this.react.getState();
    const surveys = _surveys || this.all;
    const startDate = state.surveyListFilters.dateRange.startDate;
    const endDate = state.surveyListFilters.dateRange.endDate;
    const dateType = state.surveyListFilters.dateType || "created-date";

    if (!startDate || !endDate) return surveys;

    const startDateParsed = parseISO(startDate);
    const endDateParsed = parseISO(endDate);

    const isInRange = (dateString: string | null | undefined): boolean => {
      if (!dateString) return false;
      const surveyDate = parseISO(dateString);
      if (
        isSameDay(surveyDate, startDateParsed) ||
        isSameDay(surveyDate, endDateParsed)
      ) {
        return true;
      }
      if (
        isAfter(surveyDate, startDateParsed) &&
        isBefore(surveyDate, endDateParsed)
      ) {
        return true;
      }
      return false;
    };

    if (dateType === "submitted-date" || dateType === "completed-date") {
      const orders = options?.orders ?? state.sink?.orders ?? [];
      const ordersBySurveyId = new Map<number, typeof orders>();
      orders.forEach((order) => {
        const surveyId = order?.survey_id;
        if (typeof surveyId !== "number") return;
        const existing = ordersBySurveyId.get(surveyId);
        if (existing) existing.push(order);
        else ordersBySurveyId.set(surveyId, [order]);
      });

      const dateField =
        dateType === "submitted-date" ? "submitted" : "completed";

      return surveys.filter((survey) => {
        const surveyOrders = ordersBySurveyId.get(survey.id) ?? [];
        return surveyOrders.some((order) => isInRange(order[dateField]));
      });
    }

    return surveys.filter((survey) => {
      let surveyDateString: string | null;
      if (dateType === "locked-date") {
        surveyDateString = survey.locked_time;
      } else if (dateType === "created-date") {
        surveyDateString = (survey as SurveyWithExtras).created_at;
      } else {
        surveyDateString = survey.nominal_flight_date;
      }

      if (
        (dateType === "locked-date" || dateType === "created-date") &&
        !surveyDateString
      ) {
        return false;
      }

      return isInRange(surveyDateString);
    });
  }

  public static bySearchString(_surveys?: Survey[], options?): Survey[] {
    // Searches all search terms separately across label, site name, tag labels, and order type labels,
    // plus exact numeric matches on survey ID and order ID. Search must match _all_ terms.
    // Folder is intentionally filter-only (selectedFolderIds), not searchable.
    const surveys = _surveys || this.all;
    const state = this.react.getState();
    const { surveyListFilters } = state;
    const surveySearchString = surveyListFilters.searchString;
    if (!surveySearchString) return surveys;
    const sink = state.sink;
    const sites = sink?.sites ?? [];

    const terms = surveySearchString
      .split(" ")
      .map((t) => normalizeForCompare(t))
      .filter((t) => t.length > 0);

    if (terms.length === 0) {
      return surveys;
    }

    const siteLabelById = new Map<number, string>();
    sites.forEach((site) => {
      if (site && typeof site.id === "number" && typeof site.label === "string") {
        siteLabelById.set(site.id, normalizeForCompare(site.label));
      }
    });

    const tagLabelById = new Map<number, string>();
    (options?.tags ?? []).forEach((tag) => {
      if (tag && typeof tag.id === "number" && typeof tag.label === "string") {
        tagLabelById.set(tag.id, normalizeForCompare(tag.label));
      }
    });

    const orders = options?.orders ?? sink?.orders ?? [];
    const orderLabelsBySurveyIdRaw = buildOrderTypeLabelsBySurveyId(orders);

    const orderIdsBySurveyId = new Map<number, string[]>();
    orders.forEach((order) => {
      const surveyId = order?.survey_id;
      if (typeof surveyId !== "number") return;

      if (order.id == null) return;

      const existing = orderIdsBySurveyId.get(surveyId);
      const id = String(order.id);
      if (existing) existing.push(id);
      else orderIdsBySurveyId.set(surveyId, [id]);
    });

    return (
      surveys &&
      surveys.filter(
        (survey) => {
          if (!surveySearchString) {
            return true;
          }

          const labelLower =
            survey.label != null ? normalizeForCompare(String(survey.label)) : null;
          const siteNameLower =
            survey.site_id != null
              ? siteLabelById.get(survey.site_id) ?? null
              : null;

          const surveyAny = survey as SurveyWithExtras;
          const surveyTagIds = surveyAny.tags || [];
          const tagLabelsLower = surveyTagIds
            .map((id) => tagLabelById.get(id))
            .filter((label): label is string => label != null);

          const orderLabelsLower =
            (orderLabelsBySurveyIdRaw.get(survey.id) ?? []).map((l) =>
              normalizeForCompare(l)
            );

          const orderIds = orderIdsBySurveyId.get(survey.id) ?? [];

          return terms.every((term) => {
            const isNumeric = /^\d+$/.test(term);
            return (
              (labelLower && labelLower.includes(term)) ||
              (siteNameLower && siteNameLower.includes(term)) ||
              tagLabelsLower.some((label) => label.includes(term)) ||
              orderLabelsLower.some((label) => label.includes(term)) ||
              (isNumeric &&
                (String(survey.id) === term ||
                  orderIds.some((id) => id === term)))
            );
          });
        }
      )
    );
  }

  public static byMapViewport(_surveys?: Survey[], options): Survey[] {
    if (!options.viewport) {
      return _surveys;
    }

    const surveys = _surveys || this.all;

    return surveys.filter((survey) =>
      options.viewport.contains([
        survey.coordinates.longitude,
        survey.coordinates.latitude,
      ])
    );
  }

  public static byUsers(_surveys?: Survey[]): Survey[] {
    const state = this.react.getState();
    const surveys = _surveys || this.all;
    const { selectedCreatedByUserIds, selectedLockedByUserIds } =
      state.surveyListFilters;

    const hasCreatedByFilter =
      selectedCreatedByUserIds && selectedCreatedByUserIds.length > 0;
    const hasLockedByFilter =
      selectedLockedByUserIds && selectedLockedByUserIds.length > 0;

    if (!hasCreatedByFilter && !hasLockedByFilter) {
      return surveys;
    }

    return surveys.filter((survey) => {
      const matchesCreatedBy =
        !hasCreatedByFilter ||
        (survey.created_by &&
          selectedCreatedByUserIds.includes(survey.created_by));
      const matchesLockedBy =
        !hasLockedByFilter ||
        (survey.locked_by_id &&
          selectedLockedByUserIds.includes(survey.locked_by_id));

      return matchesCreatedBy && matchesLockedBy;
    });
  }

  public static byTags(_surveys?: Survey[]): Survey[] {
    const state = this.react.getState();
    const surveys = _surveys || this.all;
    const { selectedTagIds } = state.surveyListFilters;

    if (!selectedTagIds || selectedTagIds.length === 0) {
      return surveys;
    }

    return surveys.filter((survey) => {
      const surveyAny = survey as SurveyWithExtras;
      const surveyTagIds = surveyAny.tags || [];
      return selectedTagIds.some((tagId) => surveyTagIds.includes(tagId));
    });
  }

  public static bySites(_surveys?: Survey[]): Survey[] {
    const state = this.react.getState();
    const surveys = _surveys || this.all;
    const { selectedSiteIds } = state.surveyListFilters;

    if (!selectedSiteIds || selectedSiteIds.length === 0) {
      return surveys;
    }

    return surveys.filter((survey) => {
      if (survey.site_id === null || survey.site_id === undefined) {
        return false;
      }
      return selectedSiteIds.includes(survey.site_id);
    });
  }

  public static byFolders(_surveys?: Survey[]): Survey[] {
    const state = this.react.getState();
    const surveys = _surveys || this.all;
    const { selectedFolderIds } = state.surveyListFilters;

    if (!selectedFolderIds || selectedFolderIds.length === 0) {
      return surveys;
    }

    return surveys.filter((survey) => {
      // Handle surveys with null folder_id
      if (survey.folder_id === null || survey.folder_id === undefined) {
        return false;
      }
      return selectedFolderIds.includes(survey.folder_id);
    });
  }

  public static byStatus(_surveys?: Survey[]): Survey[] {
    const state = this.react.getState();
    const surveys = _surveys || this.all;
    const { selectedStatuses } = state.surveyListFilters;

    if (!selectedStatuses || selectedStatuses.length === 0) {
      return surveys;
    }

    return surveys.filter((survey) => {
      const surveyAny = survey as SurveyWithExtras;
      const surveyStatus = surveyAny.status || "created";
      const isLocked = surveyAny.locked_time !== null;

      // Check if any of the selected statuses match
      return selectedStatuses.some((selectedStatus) => {
        if (selectedStatus === "locked") {
          return isLocked;
        }
        return surveyStatus === selectedStatus;
      });
    });
  }

  public static byOrderType(_surveys?: Survey[], options?): Survey[] {
    const state = this.react.getState();
    const surveys = _surveys || this.all;
    const { selectedOrderTypes } = state.surveyListFilters;

    if (!selectedOrderTypes || selectedOrderTypes.length === 0) {
      return surveys;
    }

    const orders = options?.orders ?? state.sink?.orders ?? [];
    const orderLabelsBySurveyId = buildOrderTypeLabelsBySurveyId(orders);

    const normalizedSelected = selectedOrderTypes.map((t) =>
      normalizeForCompare(t)
    );

    return surveys.filter((survey) => {
      const labels = orderLabelsBySurveyId.get(survey.id) ?? [];
      return labels.some((label) =>
        normalizedSelected.includes(normalizeForCompare(label))
      );
    });
  }

  public static byArchived(_surveys?: Survey[]): Survey[] {
    const state = this.react.getState();
    const surveys = _surveys || this.all;
    const { showOnlyArchived } = state.surveyListFilters;

    if (showOnlyArchived) {
      return surveys.filter((survey) => isSurveyArchived(survey));
    }

    return surveys.filter((survey) => !isSurveyArchived(survey));
  }
}

export default Survey;
